/**
 * Unified Settings Store
 * Single source of truth for all strategy settings on the frontend
 * Settings are persisted on the server, not in localStorage
 *
 * Supports per-symbol settings dynamically based on symbol registry
 * Each symbol can have its own WCC and PV settings
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { WholeCandleSettings, PinningVolumeSettings, DynamicThresholdConfig, ICTSettings, DEFAULT_ICT_SETTINGS, TrailingPullbackSettings } from '../../shared/types';
import { useSymbolStore } from './symbolStore';
import { normalizeToBase } from '../utils/symbolUtils';
import { CONFIG } from '../config';

// Supported symbols - now dynamic (string type allows any symbol)
export type StrategySymbol = string;

// Get strategy symbols dynamically from symbol registry
export function getStrategySymbols(): string[] {
  return useSymbolStore.getState().symbols.map(s => s.base);
}


// Delegates to canonical normalizer in utils/symbolUtils.ts
// Preserves null return for unrecognized symbols until callers migrate to normalizeToBase
export function normalizeToBaseSymbol(symbol: string): string | null {
  if (!symbol) return null;
  const result = normalizeToBase(symbol);
  // If the result matches a known registry symbol, it's recognized
  const knownBases = useSymbolStore.getState().symbols.map(s => s.base.toUpperCase());
  if (knownBases.includes(result)) return result;
  return null;
}

// Default values matching server defaults
export const DEFAULT_WHOLE_CANDLE_SETTINGS: WholeCandleSettings = {
  enabled: true,
  wickThresholdMin: 0,
  wickThresholdMax: 25,
  skipIfHOD: false,
  skipIfLOD: false,
  hodLodRangePoints: 5,
  skipIfLowATR: false,
  atrMinThreshold: 7,
  skipIfSmallCandle: true,
  minCandleLengthPoints: 7,
  minCandleLengthPointsEs: 2,
  tpPoints: 5,
  slPoints: 25,
  sessionStartHour: 21,
  sessionEndHour: 22,
  tradeExecutionEnabled: false,
  predictorEnabled: true,
  predictorAlertThreshold: 70,
  predictorStartTrackingAt: 80,
  preemptiveSeconds: 1, // Seconds before bar close to evaluate signals (default: 1)
  updatedAt: Date.now(),
  // Confidence Scoring Settings
  confidenceEnabled: true,
  confidenceMinThreshold: 0.0,
  // Level weights (0 = disabled)
  weightEma9: 0.8,
  weightEma21: 1.0,
  weightEma200: 1.3,
  weightVwap: 1.5,
  weightFib382: 0.8,
  weightFib50: 1.2,
  weightFib618: 1.3,
  weightFib705: 1.0,  // Stop-hunt pocket
  weightFib786: 0.9,
  weightHod: 1.0,
  weightLod: 1.0,
  // Group toggles
  useEmas: true,
  useFibs: true,
  useVwap: true,
  useHodLod: true,
  confidenceLowZonePct: 30.0,
  confidenceHighZonePct: 70.0,
  confidenceBaseScore: 50.0,
  confidenceBoostPerLevel: 10.0,
  confidencePenaltyPerLevel: 15.0,
  emaStackBonus: 10.0,
  vwapAlignmentBonus: 10.0,
  levelDensityThreshold: 3,
  levelDensityPenalty: 10.0,
  nextLevelCloseThreshold: 5.0,
  nextLevelClosePenalty: 15.0,
  // Confluence Scoring Settings
  confluenceEnabled: false,
  confluenceIncludeSkipped: true,
  showOtherSymbolSignals: false,
  // "TAKE IT" Indicator Settings
  takeItEnabled: true,
  takeItThreshold: 80,
  // Skipped Signal Penalty Settings
  skippedSignalPenalty: 40,
  skippedZeroConfidence: false,
};

// Default dynamic thresholds for Pinning Volume ATR-based settings
export const DEFAULT_DYNAMIC_THRESHOLDS: DynamicThresholdConfig = {
  enabled: true,
  mappings: [
    { atrMin: 0, atrMax: 5, volumeMultiplier: 1.2, minVolumeRatio: 6, label: 'Very Low' },
    { atrMin: 5, atrMax: 10, volumeMultiplier: 1.5, minVolumeRatio: 8, label: 'Low' },
    { atrMin: 10, atrMax: 15, volumeMultiplier: 1.8, minVolumeRatio: 10, label: 'Medium' },
    { atrMin: 15, atrMax: 25, volumeMultiplier: 2.2, minVolumeRatio: 12, label: 'High' },
    { atrMin: 25, atrMax: 999, volumeMultiplier: 2.5, minVolumeRatio: 15, label: 'Very High' },
  ],
  fallbackVolumeMultiplier: 1.5,
  fallbackMinVolumeRatio: 8,
};

export const DEFAULT_PINNING_VOLUME_SETTINGS: PinningVolumeSettings = {
  enabled: true,
  volumeMultiplier: 1.5,
  volumeLookback: 20,
  minVolumeRatio: 8,
  minWickPercentage: 30,
  priorMoveLookback: 4,
  useWickDirection: false,
  requireAlignedWick: true,
  skipIfLowATR: true,
  atrMinThreshold: 7,
  skipIfHOD: false,
  skipIfLOD: false,
  hodLodRangePoints: 10,
  skipIfSmallCandle: true,
  minCandleLengthPoints: 5,
  minCandleLengthPointsEs: 2,
  tradeExecutionEnabled: false,
  tpPoints: 5,
  slPoints: 25,
  confluenceEnabled: false,
  dynamicThresholds: DEFAULT_DYNAMIC_THRESHOLDS,
};

// Trailing Candle Overlay settings
export interface TrailingCandleSettings {
  enabled: boolean;
  durationSeconds: number;  // 15, 30, 45, or 60
}

export const DEFAULT_TRAILING_CANDLE_SETTINGS: TrailingCandleSettings = {
  enabled: true,
  durationSeconds: 30,
};

// Trailing Pullback Strategy settings
export const DEFAULT_TRAILING_PULLBACK_SETTINGS: TrailingPullbackSettings = {
  enabled: true,
  confidenceThreshold: 60,
  includeContinuation: false,
  requireWccAlignment: true,
  minTrailingBodyPct: 5,
  updatedAt: Date.now(),
};

// Magnet Candle Strategy settings
export interface MagnetCandleSettings {
  enabled: boolean;
  wickThreshold: number;      // 0-25, default 5
  minBodyPct: number;         // 0-100, default 60
  minRangePoints: number;     // default 2.0
  maxActiveMagnets: number;   // default 10
  lookbackCandles: number;    // default 200
  extensionLineCount: number; // 0-10, how many recent unbreached magnets get a horizontal line to the right edge. 0 = off.
  updatedAt: number;
}

export const DEFAULT_MAGNET_CANDLE_SETTINGS: MagnetCandleSettings = {
  enabled: true,
  wickThreshold: 5,
  minBodyPct: 60,
  minRangePoints: 2.0,
  maxActiveMagnets: 10,
  lookbackCandles: 200,
  extensionLineCount: 5,
  updatedAt: 0,
};

// Break of Structure (BOS) Strategy settings
export interface BreakOfStructureSettings {
  enabled: boolean;
  swingLookback: number;        // fractal pivot strength (bars each side), default 5
  breakConfirmation: 'close' | 'wick'; // default 'close'
  minBreakPoints: number;       // buffer beyond level, default 0.0
  lineLengthBars: number;       // max bars the short level line extends, default 10
  bullColor: string;            // default '#26a69a'
  bearColor: string;            // default '#ef5350'
  lineStyle: 'solid' | 'dashed'; // default 'solid'
  lineWidth: number;            // default 2
  showLabel: boolean;           // default true
  markerShape: string;          // default 'diamond'
  showChoch: boolean;           // emit CHoCH (reversal) markers, default true
  chochColor: string;           // default '#ff9800'
  gradingEnabled: boolean;      // grade each break A+/A/B+/B + run retest phase, default true
  retestWindowBars: number;     // bars to watch for a retest before finalizing, default 10
  gradeBands: BosGradeBands;    // score thresholds for A+/A/B+
  updatedAt: number;
}

/** Grade-band score thresholds. Below b+ minimum is "B" (the floor). */
export interface BosGradeBands {
  aPlusMin: number;             // default 85
  aMin: number;                 // default 70
  bPlusMin: number;             // default 55
}

export const DEFAULT_BOS_SETTINGS: BreakOfStructureSettings = {
  enabled: true,
  swingLookback: 5,
  breakConfirmation: 'close',
  minBreakPoints: 0.0,
  lineLengthBars: 10,
  bullColor: '#26a69a',
  bearColor: '#ef5350',
  lineStyle: 'solid',
  lineWidth: 2,
  showLabel: true,
  markerShape: 'diamond',
  showChoch: true,
  chochColor: '#ff9800',
  gradingEnabled: true,
  retestWindowBars: 10,
  gradeBands: { aPlusMin: 85, aMin: 70, bPlusMin: 55 },
  updatedAt: 0,
};

export interface SRProximityAlertSettings {
  enabled: boolean;
  disabledSymbols: string[];
  alertTimeframes: string[];
  atrMultiplier: number;
  alertOnApproaching: boolean;
  alertOnBreach: boolean;
  cooldownMinutes: number;
  updatedAt: number;
}

export const DEFAULT_SR_PROXIMITY_ALERT_SETTINGS: SRProximityAlertSettings = {
  enabled: true,
  disabledSymbols: [],
  alertTimeframes: ['D', '4h', '1h'],
  atrMultiplier: 0.5,
  alertOnApproaching: true,
  alertOnBreach: true,
  cooldownMinutes: 15,
  updatedAt: 0,
};

// Per-symbol settings storage
type PerSymbolSettings<T> = Record<StrategySymbol, T>;

interface SettingsState {
  // Global settings (used when no per-symbol override exists)
  wholeCandle: WholeCandleSettings;
  pinningVolume: PinningVolumeSettings;
  ict: ICTSettings;
  trailingCandle: TrailingCandleSettings;
  trailingPullbackSettings: TrailingPullbackSettings;
  magnetCandle: MagnetCandleSettings;
  bos: BreakOfStructureSettings;
  srProximityAlerts: SRProximityAlertSettings;
  // Per-symbol settings (overrides global when set)
  perSymbolWCC: Partial<PerSymbolSettings<WholeCandleSettings>>;
  perSymbolPV: Partial<PerSymbolSettings<PinningVolumeSettings>>;
  perSymbolICT: Partial<PerSymbolSettings<ICTSettings>>;
  perSymbolMagnetCandle: Partial<PerSymbolSettings<MagnetCandleSettings>>;

  // Currently selected symbol for settings UI
  selectedSymbol: StrategySymbol | 'global';

  // Loading and sync state
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncError: string | null;
  lastSyncAt: number | null;

  // Actions for Whole Candle settings (global)
  setWholeCandleSettings: (settings: WholeCandleSettings) => void;
  updateWholeCandleSettings: (updates: Partial<WholeCandleSettings>) => void;

  // Actions for Pinning Volume settings (global)
  setPinningVolumeSettings: (settings: PinningVolumeSettings) => void;
  updatePinningVolumeSettings: (updates: Partial<PinningVolumeSettings>) => void;

  // Actions for ICT settings (global)
  setICTSettings: (settings: ICTSettings) => void;
  updateICTSettings: (updates: Partial<ICTSettings>) => void;

  // Actions for Trailing Candle settings (global)
  setTrailingCandleSettings: (settings: TrailingCandleSettings) => void;
  updateTrailingCandleSettings: (updates: Partial<TrailingCandleSettings>) => void;

  // Actions for Trailing Pullback settings (global)
  setTrailingPullbackSettings: (settings: TrailingPullbackSettings) => void;
  fetchTrailingPullbackSettings: () => Promise<void>;
  saveTrailingPullbackSettings: (settings: TrailingPullbackSettings) => Promise<void>;

  // Actions for Magnet Candle settings (global)
  setMagnetCandleSettings: (settings: MagnetCandleSettings) => void;
  updateMagnetCandleSettings: (updates: Partial<MagnetCandleSettings>) => void;

  // Actions for Break of Structure (BOS) settings (global)
  setBosSettings: (settings: BreakOfStructureSettings) => void;
  updateBosSettings: (updates: Partial<BreakOfStructureSettings>) => void;

  // Actions for S/R Proximity Alert settings (global)
  setSRProximityAlertSettings: (settings: SRProximityAlertSettings) => void;
  updateSRProximityAlertSettings: (updates: Partial<SRProximityAlertSettings>) => void;

  // Per-symbol actions
  setSelectedSymbol: (symbol: StrategySymbol | 'global') => void;
  setWholeCandleForSymbol: (symbol: StrategySymbol, settings: WholeCandleSettings) => void;
  updateWholeCandleForSymbol: (symbol: StrategySymbol, updates: Partial<WholeCandleSettings>) => void;
  setPinningVolumeForSymbol: (symbol: StrategySymbol, settings: PinningVolumeSettings) => void;
  updatePinningVolumeForSymbol: (symbol: StrategySymbol, updates: Partial<PinningVolumeSettings>) => void;
  setICTForSymbol: (symbol: StrategySymbol, settings: ICTSettings) => void;
  updateICTForSymbol: (symbol: StrategySymbol, updates: Partial<ICTSettings>) => void;
  setMagnetCandleForSymbol: (symbol: StrategySymbol, settings: MagnetCandleSettings) => void;
  updateMagnetCandleForSymbol: (symbol: StrategySymbol, updates: Partial<MagnetCandleSettings>) => void;

  // Get effective settings for a symbol (per-symbol if exists, else global)
  getWholeCandleForSymbol: (symbol: StrategySymbol) => WholeCandleSettings;
  getPinningVolumeForSymbol: (symbol: StrategySymbol) => PinningVolumeSettings;
  getICTForSymbol: (symbol: StrategySymbol) => ICTSettings;
  getMagnetCandleForSymbol: (symbol: StrategySymbol) => MagnetCandleSettings;

  // Sync state actions
  setLoading: (loading: boolean) => void;
  setSyncing: (syncing: boolean) => void;
  setSyncError: (error: string | null) => void;
  setSyncSuccess: () => void;

  // Reset to defaults
  resetWholeCandleToDefaults: () => void;
  resetPinningVolumeToDefaults: () => void;
  resetICTToDefaults: () => void;
  resetBosToDefaults: () => void;
  resetSymbolToGlobal: (symbol: StrategySymbol, strategy: 'wcc' | 'pv' | 'ict' | 'magnet-candle') => void;
}

export const useSettingsStore = create<SettingsState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    wholeCandle: DEFAULT_WHOLE_CANDLE_SETTINGS,
    pinningVolume: DEFAULT_PINNING_VOLUME_SETTINGS,
    ict: DEFAULT_ICT_SETTINGS,
    trailingCandle: DEFAULT_TRAILING_CANDLE_SETTINGS,
    trailingPullbackSettings: DEFAULT_TRAILING_PULLBACK_SETTINGS,
    magnetCandle: DEFAULT_MAGNET_CANDLE_SETTINGS,
    bos: DEFAULT_BOS_SETTINGS,
    srProximityAlerts: DEFAULT_SR_PROXIMITY_ALERT_SETTINGS,
    perSymbolWCC: {},
    perSymbolPV: {},
    perSymbolICT: {},
    perSymbolMagnetCandle: {},
    selectedSymbol: 'global',
    isLoading: true,
    isSyncing: false,
    lastSyncError: null,
    lastSyncAt: null,

    // Global Whole Candle actions
    setWholeCandleSettings: (settings) =>
      set({ wholeCandle: settings }),

    updateWholeCandleSettings: (updates) =>
      set((state) => ({
        wholeCandle: { ...state.wholeCandle, ...updates },
      })),

    // Global Pinning Volume actions
    setPinningVolumeSettings: (settings) =>
      set({ pinningVolume: settings }),

    updatePinningVolumeSettings: (updates) =>
      set((state) => ({
        pinningVolume: { ...state.pinningVolume, ...updates },
      })),

    // Global ICT actions
    setICTSettings: (settings) =>
      set({ ict: settings }),

    updateICTSettings: (updates) =>
      set((state) => ({
        ict: { ...state.ict, ...updates },
      })),

    // Global Trailing Candle actions
    setTrailingCandleSettings: (settings) =>
      set({ trailingCandle: settings }),

    updateTrailingCandleSettings: (updates) =>
      set((state) => ({
        trailingCandle: { ...state.trailingCandle, ...updates },
      })),

    // Global Trailing Pullback actions
    setTrailingPullbackSettings: (settings) =>
      set({ trailingPullbackSettings: settings }),

    fetchTrailingPullbackSettings: async () => {
      try {
        const response = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-pullback-settings`);
        if (response.ok) {
          const settings = await response.json();
          const converted: TrailingPullbackSettings = {
            enabled: settings.enabled ?? DEFAULT_TRAILING_PULLBACK_SETTINGS.enabled,
            confidenceThreshold: settings.confidenceThreshold ?? DEFAULT_TRAILING_PULLBACK_SETTINGS.confidenceThreshold,
            includeContinuation: settings.includeContinuation ?? DEFAULT_TRAILING_PULLBACK_SETTINGS.includeContinuation,
            requireWccAlignment: settings.requireWccAlignment ?? DEFAULT_TRAILING_PULLBACK_SETTINGS.requireWccAlignment,
            minTrailingBodyPct: settings.minTrailingBodyPct ?? DEFAULT_TRAILING_PULLBACK_SETTINGS.minTrailingBodyPct,
            updatedAt: settings.updatedAt ?? Date.now(),
          };
          set({ trailingPullbackSettings: converted });
        }
      } catch (error) {
        console.error('Failed to fetch trailing pullback settings:', error);
      }
    },

    saveTrailingPullbackSettings: async (settings) => {
      try {
        const payload = {
          enabled: settings.enabled,
          confidenceThreshold: settings.confidenceThreshold,
          includeContinuation: settings.includeContinuation,
          requireWccAlignment: settings.requireWccAlignment,
          minTrailingBodyPct: settings.minTrailingBodyPct,
          updatedAt: Date.now(),
        };
        const response = await fetch(`${CONFIG.getBackendUrl(window.location.hostname)}/api/trailing-pullback-settings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          const saved = await response.json();
          const converted: TrailingPullbackSettings = {
            enabled: saved.enabled,
            confidenceThreshold: saved.confidenceThreshold,
            includeContinuation: saved.includeContinuation,
            requireWccAlignment: saved.requireWccAlignment,
            minTrailingBodyPct: saved.minTrailingBodyPct,
            updatedAt: saved.updatedAt,
          };
          set({ trailingPullbackSettings: converted });
        }
      } catch (error) {
        console.error('Failed to save trailing pullback settings:', error);
      }
    },

    // Global Magnet Candle actions
    setMagnetCandleSettings: (settings) =>
      set({ magnetCandle: settings }),

    updateMagnetCandleSettings: (updates) =>
      set((state) => ({
        magnetCandle: { ...state.magnetCandle, ...updates },
      })),

    // Global Break of Structure (BOS) actions
    setBosSettings: (settings) =>
      set({ bos: settings }),

    updateBosSettings: (updates) =>
      set((state) => ({
        bos: { ...state.bos, ...updates },
      })),

    // Global S/R Proximity Alert actions
    setSRProximityAlertSettings: (settings) =>
      set({ srProximityAlerts: settings }),

    updateSRProximityAlertSettings: (updates) =>
      set((state) => ({
        srProximityAlerts: { ...state.srProximityAlerts, ...updates },
      })),

    // Per-symbol actions
    setSelectedSymbol: (symbol) =>
      set({ selectedSymbol: symbol }),

    setWholeCandleForSymbol: (symbol, settings) => {
      const key = normalizeToBase(symbol);
      set((state) => ({
        perSymbolWCC: { ...state.perSymbolWCC, [key]: settings },
      }));
    },

    updateWholeCandleForSymbol: (symbol, updates) => {
      const key = normalizeToBase(symbol);
      set((state) => {
        const current = state.perSymbolWCC[key] || state.wholeCandle;
        return {
          perSymbolWCC: {
            ...state.perSymbolWCC,
            [key]: { ...current, ...updates },
          },
        };
      });
    },

    setPinningVolumeForSymbol: (symbol, settings) => {
      const key = normalizeToBase(symbol);
      set((state) => ({
        perSymbolPV: { ...state.perSymbolPV, [key]: settings },
      }));
    },

    updatePinningVolumeForSymbol: (symbol, updates) => {
      const key = normalizeToBase(symbol);
      set((state) => {
        const current = state.perSymbolPV[key] || state.pinningVolume;
        return {
          perSymbolPV: {
            ...state.perSymbolPV,
            [key]: { ...current, ...updates },
          },
        };
      });
    },

    setICTForSymbol: (symbol, settings) => {
      const key = normalizeToBase(symbol);
      set((state) => ({
        perSymbolICT: { ...state.perSymbolICT, [key]: settings },
      }));
    },

    updateICTForSymbol: (symbol, updates) => {
      const key = normalizeToBase(symbol);
      set((state) => {
        const current = state.perSymbolICT[key] || state.ict;
        return {
          perSymbolICT: {
            ...state.perSymbolICT,
            [key]: { ...current, ...updates },
          },
        };
      });
    },

    // Get effective settings for a symbol
    getWholeCandleForSymbol: (symbol) => {
      const state = get();
      const key = normalizeToBase(symbol);
      return state.perSymbolWCC[key] || state.wholeCandle;
    },

    getPinningVolumeForSymbol: (symbol) => {
      const state = get();
      const key = normalizeToBase(symbol);
      return state.perSymbolPV[key] || state.pinningVolume;
    },

    getICTForSymbol: (symbol) => {
      const state = get();
      const key = normalizeToBase(symbol);
      return state.perSymbolICT[key] || state.ict;
    },

    setMagnetCandleForSymbol: (symbol, settings) => {
      const key = normalizeToBase(symbol);
      set((state) => ({
        perSymbolMagnetCandle: { ...state.perSymbolMagnetCandle, [key]: settings },
      }));
    },

    updateMagnetCandleForSymbol: (symbol, updates) => {
      const key = normalizeToBase(symbol);
      set((state) => {
        const current = state.perSymbolMagnetCandle[key] || state.magnetCandle;
        return {
          perSymbolMagnetCandle: {
            ...state.perSymbolMagnetCandle,
            [key]: { ...current, ...updates },
          },
        };
      });
    },

    getMagnetCandleForSymbol: (symbol) => {
      const state = get();
      const key = normalizeToBase(symbol);
      return state.perSymbolMagnetCandle[key] || state.magnetCandle;
    },

    // Sync state actions
    setLoading: (loading) => set({ isLoading: loading }),

    setSyncing: (syncing) => set({ isSyncing: syncing }),

    setSyncError: (error) =>
      set({ lastSyncError: error, isSyncing: false }),

    setSyncSuccess: () =>
      set({ lastSyncError: null, isSyncing: false, lastSyncAt: Date.now() }),

    // Reset actions
    resetWholeCandleToDefaults: () =>
      set({ wholeCandle: DEFAULT_WHOLE_CANDLE_SETTINGS }),

    resetPinningVolumeToDefaults: () =>
      set({ pinningVolume: DEFAULT_PINNING_VOLUME_SETTINGS }),

    resetICTToDefaults: () =>
      set({ ict: DEFAULT_ICT_SETTINGS }),

    resetBosToDefaults: () =>
      set({ bos: DEFAULT_BOS_SETTINGS }),

    resetSymbolToGlobal: (symbol, strategy) => {
      const key = normalizeToBase(symbol);
      set((state) => {
        if (strategy === 'wcc') {
          const { [key]: _, ...rest } = state.perSymbolWCC;
          return { perSymbolWCC: rest };
        } else if (strategy === 'pv') {
          const { [key]: _, ...rest } = state.perSymbolPV;
          return { perSymbolPV: rest };
        } else if (strategy === 'ict') {
          const { [key]: _, ...rest } = state.perSymbolICT;
          return { perSymbolICT: rest };
        } else {
          const { [key]: _, ...rest } = state.perSymbolMagnetCandle;
          return { perSymbolMagnetCandle: rest };
        }
      });
    },
  }))
);

// Convenience selectors
export const selectWholeCandleEnabled = (state: SettingsState) =>
  state.wholeCandle.enabled;

export const selectPinningVolumeEnabled = (state: SettingsState) =>
  state.pinningVolume.enabled;

export const selectICTEnabled = (state: SettingsState) =>
  state.ict.enabled;

export const selectTrailingPullbackEnabled = (state: SettingsState) =>
  state.trailingPullbackSettings.enabled;

export const selectMagnetCandleEnabled = (state: SettingsState) =>
  state.magnetCandle.enabled;


